/**
 * Open a dialog, close it, and give the keyboard back where it was.
 *
 * Eight dialogs had written this by hand and all eight had the same two holes.
 *
 * ## The hole everybody had: focus never came back
 *
 * Each one focused itself on open - so a screen reader announces it and Tab
 * starts inside it - and none of them restored focus when it closed. After
 * Escape, `document.activeElement` was `<body>`, which for somebody navigating
 * by keyboard means the next Tab restarts at the top of the page: past the
 * skip link, the whole sidebar, and back down to wherever they were. The
 * dialog is usually opened from one specific button, and that button is the
 * place to come back to.
 *
 * ## The hole two of them had: focus stolen mid-typing
 *
 * `EditProjectDialog` and `DeleteProjectDialog` put the focus call in an
 * effect that also read `busy`, so the effect re-ran on every save - pulling
 * focus out of the field the person was in and back onto the dialog, twice per
 * save. Focusing is a mount concern and belongs in an effect that runs once;
 * the key handler needs the current `busy` and is kept separate, in a ref, so
 * it can be current without re-running the mount effect.
 *
 * ## What it deliberately does not do
 *
 * It does not trap Tab. Every one of these dialogs is rendered above the page
 * rather than in a portal with the rest inert, so a real trap means managing a
 * tabbable-node list that changes as the dialog's own contents change. That is
 * a bigger change than this release should carry, and a half-built trap is
 * worse than none: it strands people. Escape closes, focus returns, and the
 * page behind stays reachable - which is honest about what is implemented.
 */

import { useEffect, useRef, type RefObject } from "react";

interface Options {
  /**
   * Escape, when the dialog is willing to close. Omit for one that is not.
   *
   * `| undefined` is spelled out because this project sets
   * `exactOptionalPropertyTypes`: a caller that computes the handler and gets
   * `undefined` is doing the same thing as omitting it, and should not have to
   * build a different object to say so.
   */
  onEscape?: (() => void) | undefined;
  /**
   * Read at the moment Escape arrives, not when the hook was set up. A dialog
   * mid-save refuses to close, and that answer must be the current one.
   */
  escapeEnabled?: boolean | undefined;
}

export function useDialogFocus(
  /** What takes focus on open: the dialog itself, or its close button. */
  target: RefObject<HTMLElement | null>,
  { onEscape, escapeEnabled = true }: Options = {},
): void {
  // Kept current every render, read only from inside the listener, so the
  // listener is always right without the mount effect ever re-running.
  const escape = useRef<(() => void) | undefined>(undefined);
  escape.current = escapeEnabled ? onEscape : undefined;

  useEffect(() => {
    const opener = document.activeElement;
    target.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") escape.current?.();
    };
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
      // Only back to something still on the page and still focusable. A
      // dialog that deleted the row it was opened from has no opener left,
      // and focusing a detached node throws away the focus entirely.
      if (
        opener instanceof HTMLElement &&
        opener.isConnected &&
        opener !== document.body
      ) {
        opener.focus();
      }
    };
    // Mount and unmount only. Everything that changes is read through a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
